import { ColumnSpanConfig } from "../blocks/layout/GridColumn.types";
import { SectionItemConfig } from "../render/contentConfig.types";
import { TableTabConfig } from "./tableConfig.types";
import { ViewConditionOperator } from "./viewConditionOperators";

/**
 * Bump this when the shape changes in a way a stored layout can't be read as-is, and
 * add the step that upgrades older layouts where they are read. Ticketing reads the
 * shape too (`FieldNamesFromConfig`, the conditions it copies out), so check it.
 */
export const PageConfigSchemaVersion = 1;

// Ids are stable across edits, so selection and reorder key on them rather than on a
// field name an unbound field doesn't have yet. Either a field or a content block.
export type PageConfigField = SectionItemConfig & { id: string };

export interface PageConfigSectionBlock {
  kind: "section";
  id: string;
  title: string;
  helperText?: string;
  fields: PageConfigField[];
  /** Shown only while this rule in `visibilityRules` holds. */
  visibleWhen?: string;
  /** A `fieldComponents` key drawn beside the title, such as an Add button. */
  headerComponentKey?: string;
  /**
   * A step of the new-record wizard: before the full page, each such section is shown
   * alone and full width, in the order they appear.
   */
  wizardStep?: boolean;
  /**
   * For a section holding one component: draws it straight in the column, with no section,
   * grid or field around it, so it can fill the column's height.
   */
  bare?: boolean;
  /** Where this step comes in the wizard, lowest first; the layout order when absent. */
  wizardOrder?: number;
}

/**
 * The entity's related-record tables, positioned like any other block. A column names
 * a display format rather than holding a render function, so the block survives JSON.
 */
export interface PageConfigTablesBlock {
  kind: "tables";
  id: string;
  /** A heading over the tabs, for tables with more than one tab to name them. */
  title?: string;
  tabs: TableTabConfig[];
}

export type PageConfigBlock = PageConfigSectionBlock | PageConfigTablesBlock;

export interface PageConfigColumn {
  id: string;
  span: ColumnSpanConfig;
  /**
   * Draws the whole column as one card, and the sections inside it plain, so a run of
   * related sections reads as one thing rather than as three boxes. Absent is the
   * default every view saved so far means: each section is its own card.
   */
  card?: boolean;
  /**
   * Only inside a card: the card grows to the height of the tallest column in its row,
   * rather than stopping under its last section.
   */
  fillHeight?: boolean;
  /** Blocks stacked top-to-bottom within this column. */
  blocks: PageConfigBlock[];
}

export interface PageConfigRow {
  id: string;
  columns: PageConfigColumn[];
}

/**
 * When a view applies to a record: how the attribute compares with the value. Compared
 * server-side as text where the operator is equality, trimmed and case-insensitive - a
 * picklist value is a number in the record and the option's own string here.
 */
export interface ViewCondition {
  /** Attribute name, from the entity's catalog. */
  field: string;
  value: string;
  /**
   * Absent means equality, which is what every condition saved before operators
   * existed means. `FormViewResolver` reads it the same way.
   */
  operator?: ViewConditionOperator;
}

/**
 * The persisted shape of a page. Pure data, so it can be stored and edited.
 * `pageType` is carried so a "list" type can be added later.
 */
export interface PageConfig {
  schemaVersion: number;
  entityName: string;
  viewId: string;
  pageType: "record";
  rows: PageConfigRow[];
  /**
   * All of these must match for the view to apply; absent or empty applies to every
   * record. Inside the config so versions restore them with the layout.
   */
  conditions?: ViewCondition[];
}
